import { useCallback, useMemo, useState } from "react"

import { DropResolution } from "./resolveColumnDrop"
import { Column, ColumnType, GroupColumn } from "../../useTable/types"

export type ColumnReorderFn = (drop: DropResolution) => void

interface ColumnOrderResult<T> {
  columns: Column<T>[]
  reorderColumn: ColumnReorderFn
}

export function useColumnOrder<T>(
  initial: Column<T>[],
  isEnabled: boolean,
  lockedColumnsCount = 0,
  onReorder?: (columns: Column<T>[]) => void
): ColumnOrderResult<T> {
  const [reorderedColumns, setReorderedColumns] = useState<
    Column<T>[] | undefined
  >(undefined)

  const columns = useMemo(() => {
    if (!isEnabled) return initial

    if (onReorder) return initial

    if (!reorderedColumns) return initial

    const initialMap = new Map(initial.map((c) => [c.title, c]))
    const reorderedTitles = reorderedColumns.map((c) => c.title)
    const reorderedSet = new Set(reorderedTitles)
    const sameOrderAndLength =
      initialMap.size === reorderedSet.size &&
      [...initialMap.keys()].every((t) => reorderedSet.has(t))

    if (sameOrderAndLength) return reorderedColumns

    const kept = reorderedTitles
      .filter((title) => initialMap.has(title))
      .map((title) => initialMap.get(title)!)
    const keptTitles = new Set(kept.map((c) => c.title))
    const newCols = initial.filter((c) => !keptTitles.has(c.title))
    const merged = [...kept, ...newCols]

    setReorderedColumns(merged)

    return merged
  }, [initial, isEnabled, reorderedColumns, onReorder])

  const reorderColumn = useCallback(
    ({
      sourceGroupTitle,
      columnTitle,
      destGroupTitle,
      overColumnTitle,
    }: DropResolution) => {
      if (!isEnabled) return

      const currentColumns = onReorder ? initial : reorderedColumns || initial
      const hasGroups = currentColumns.some((c) => c.type === ColumnType.Group)

      // No Group columns
      if (!hasGroups) {
        const sourceIdx = currentColumns.findIndex(
          (c) => c.title === columnTitle
        )
        if (sourceIdx === -1) return

        // Don't allow reordering locked columns (group by rows)
        if (sourceIdx < lockedColumnsCount) return

        const next = [...currentColumns]
        const [moved] = next.splice(sourceIdx, 1)

        let insertIdx = next.length
        if (overColumnTitle) {
          const baseIdx = next.findIndex((c) => c.title === overColumnTitle)
          if (baseIdx !== -1) insertIdx = baseIdx
        }

        // Don't allow moving regular columns before locked columns
        if (insertIdx < lockedColumnsCount) {
          insertIdx = lockedColumnsCount
        }

        next.splice(insertIdx, 0, moved)

        if (onReorder) {
          onReorder(next)
        } else {
          setReorderedColumns(next)
        }
        return
      }

      const sourceGroup = currentColumns.find(
        (c): c is GroupColumn<T> =>
          c.type === ColumnType.Group && c.title === sourceGroupTitle
      )
      const destGroup = currentColumns.find(
        (c): c is GroupColumn<T> =>
          c.type === ColumnType.Group && c.title === destGroupTitle
      )

      if (!sourceGroup || !destGroup) return

      const sourceGroupIdx = currentColumns.indexOf(sourceGroup)
      const destGroupIdx = currentColumns.indexOf(destGroup)
      const sameGroup = sourceGroupIdx === destGroupIdx

      if (sameGroup && overColumnTitle === columnTitle) return

      // Find source column index
      const sourceIdx = sourceGroup.columns.findIndex(
        (c) => c.title === columnTitle
      )
      if (sourceIdx === -1) return

      // Remove source column and store moved column
      const sourceCols = [...sourceGroup.columns]
      const [moved] = sourceCols.splice(sourceIdx, 1)

      // Locate destination
      const destCols = sameGroup ? sourceCols : [...destGroup.columns]

      let insertIdx = destCols.length
      if (overColumnTitle) {
        const baseIdx = destCols.findIndex((c) => c.title === overColumnTitle)
        if (baseIdx !== -1) {
          // Always insert before the target column
          insertIdx = baseIdx
        }
      }

      destCols.splice(insertIdx, 0, moved)

      const result = [...currentColumns]
      result[sourceGroupIdx] = {
        ...sourceGroup,
        columns: sameGroup ? destCols : sourceCols,
      }

      if (!sameGroup) {
        result[destGroupIdx] = { ...destGroup, columns: destCols }
      }

      if (onReorder) {
        onReorder(result)
      } else {
        setReorderedColumns(result)
      }
    },
    [
      isEnabled,
      initial,
      lockedColumnsCount,
      onReorder,
      reorderedColumns,
      setReorderedColumns,
    ]
  )

  return {
    columns,
    reorderColumn,
  }
}
